import React, { lazy } from 'react';
import { Navigate, useParams } from 'react-router';
import { TenantRouteMeta } from '../types';
import { useCurrentUser } from '../../auth/useCurrentUser';
import { paths } from '../paths';

const MixpanelFlags = lazy(() => import('../../pages/MixpanelFlags/MixpanelFlags'));
const Dashboard = lazy(() => import('../../pages/Dashboard/Dashboard'));
const GoLiveChecklist = lazy(() => import('../../pages/GoLive/GoLiveChecklist'));
const ActivityFeed = lazy(() => import('../../pages/Activity/ActivityFeed'));
const PipelineMonitorReal = lazy(() => import('../../pages/Pipeline/PipelineMonitorReal'));
const JobHistory = lazy(() => import('../../pages/Pipeline/JobHistory'));
const ConsumerDashboard = lazy(() => import('../../pages/Consumer/ConsumerDashboard'));
const CardOperations = lazy(() => import('../../pages/CardOperations/CardOperations'));
const Users = lazy(() => import('../../pages/Users/Users'));
const Consumer360 = lazy(() => import('../../pages/Consumer360/Consumer360'));
const TransactionHistory = lazy(() => import('../../pages/Transactions/TransactionHistory'));
const CardFundingDashboard = lazy(() => import('../../pages/CardFunding/CardFundingDashboard'));
const HealthProgramDashboard = lazy(() =>
  import('../../pages/HealthProgram/HealthProgramDashboard'),
);
const AnalyticsDashboard = lazy(() => import('../../pages/Analytics/AnalyticsDashboard'));
const InsightsPage = lazy(() => import('../../pages/Insights/InsightsPage'));

/**
 * RBAC_V2 (R12/design §3.4/§5) — `/analytics` and `/insights` mutually
 * redirect based on the flag, so the new `insights` surface doesn't exist
 * pre-flip and the retired `analytics` path keeps working as a permanent
 * redirect post-flip. Kept as tiny wrappers (not inlined onto the route
 * `element`) so each side stays a one-branch component: flag on ⇒ Insights
 * (CPT-1177/1178/1179's per-region client-reports UI) is the destination;
 * flag off ⇒ Analytics renders directly, exactly as it always has (role
 * 'admin' unenforced — untouched by this change).
 *
 * CPT-1177/1178/1179: InsightsRoute previously rendered AnalyticsDashboard
 * (the Tableau embed) as a temporary stand-in — this is the work package
 * that replaces it with the real client-reports Reports/Manage UI.
 */
export const AnalyticsRoute: React.FC = () => {
  const { tenantCode } = useParams<{ tenantCode: string }>();
  const { rbacV2Enabled } = useCurrentUser();
  if (rbacV2Enabled && tenantCode) {
    return <Navigate to={paths.insights(tenantCode)} replace />;
  }
  return <AnalyticsDashboard />;
};

export const InsightsRoute: React.FC = () => {
  const { tenantCode } = useParams<{ tenantCode: string }>();
  const { rbacV2Enabled } = useCurrentUser();
  if (!rbacV2Enabled && tenantCode) {
    return <Navigate to={paths.analytics(tenantCode)} replace />;
  }
  return <InsightsPage />;
};

export const observabilityRoutes: TenantRouteMeta[] = [
  // ── Dashboard group (single leaf) ──────────────────────────────
  {
    path: 'dashboard',
    element: <Dashboard />,
    permission: 'dashboard',
    sidebar: { group: 'dashboard', label: 'Dashboard' },
  },

  // ── Consumer 360 group ─────────────────────────────────────────
  // Historical: the "Consumer 360" sidebar item points to /users
  // (the users list), with the per-consumer detail view at /consumer360.
  {
    path: 'users',
    element: <Users />,
    permission: 'consumer_360',
    sidebar: { group: 'consumer-360', label: 'Consumer 360' },
  },
  {
    path: 'consumer360',
    element: <Consumer360 />,
    permission: 'consumer_360',
    // Accessible via deep link; not a sidebar entry of its own.
  },
  {
    path: 'consumer360/:consumerId',
    element: <Consumer360 />,
    permission: 'consumer_360',
  },
  {
    path: 'transactions',
    element: <TransactionHistory />,
    permission: 'consumer_360',
    // Accessible from Consumer 360; not in sidebar.
  },

  // ── Operations group ────────────────────────────────────────────
  {
    path: 'pipelines',
    element: <PipelineMonitorReal />,
    role: 'admin',
    permission: 'operations',
    sidebar: { group: 'operations', label: 'Pipeline Monitor', order: 1 },
  },
  {
    path: 'pipelines/jobs',
    element: <JobHistory />,
    role: 'admin',
    permission: 'operations',
    sidebar: { group: 'operations', label: 'Job History', order: 2 },
  },
  {
    path: 'card-funding',
    element: <CardFundingDashboard />,
    role: 'admin',
    permission: 'operations',
    sidebar: { group: 'operations', label: 'Card Funding', order: 3 },
  },
  {
    path: 'card-operations',
    element: <CardOperations />,
    role: 'admin',
    permission: 'operations',
    sidebar: { group: 'operations', label: 'Card Operations', order: 4 },
  },
  {
    path: 'health-program',
    element: <HealthProgramDashboard />,
    role: 'admin',
    permission: 'operations',
    sidebar: { group: 'operations', label: 'Health Program', order: 5 },
  },
  {
    path: 'consumers',
    element: <ConsumerDashboard />,
    role: 'admin',
    permission: 'operations',
    sidebar: { group: 'operations', label: 'Consumer Dashboard', order: 6 },
  },
  {
    path: 'activity',
    element: <ActivityFeed />,
    role: 'admin',
    permission: 'operations',
    sidebar: { group: 'operations', label: 'Activity Feed', order: 7 },
  },
  {
    path: 'go-live',
    element: <GoLiveChecklist />,
    role: 'admin',
    permission: 'operations',
    sidebar: { group: 'operations', label: 'Go-Live Checklist', order: 8 },
  },

  // ── Analytics (single leaf) ─────────────────────────────────────
  // RBAC_V2 (R12): under the flag this route is replaced in the sidebar by
  // `insights` (groupsConfig.ts `hiddenWhenV2`/`onlyWhenV2`) and redirects to
  // it (AnalyticsRoute). Left WITHOUT a `permission` deliberately — it must
  // stay reachable-and-redirecting for EVERY authenticated user under the
  // flag (old bookmarks), not gated to a surface. Flag off is unaffected
  // (unenforced `role: 'admin'`, exactly as today — AnalyticsRoute renders
  // AnalyticsDashboard directly).
  {
    path: 'analytics',
    element: <AnalyticsRoute />,
    role: 'admin',
    sidebar: { group: 'analytics', label: 'Analytics' },
  },

  // ── Insights (NEW, R12) — same component as Analytics, new permission
  // pairing. Gated anyOf(insights_manager, insights_client) so an
  // insights_client without hide_chrome still gets a nav entry; super-admin
  // sees it via hasPermission's implicit bypass. Flag off: InsightsRoute
  // redirects back to /analytics, so this route exists but leads nowhere new
  // pre-flip (no permission metadata is consulted on the flag-off path).
  {
    path: 'insights',
    element: <InsightsRoute />,
    permissionAnyOf: ['insights_manager', 'insights_client'],
    sidebar: { group: 'insights', label: 'Insights' },
  },

  // ── Feature Flags — Mixpanel READ-ONLY viewer ────────────────────
  // Shows Mixpanel flag state evaluated for this tenant using a
  // representative synthetic distinct_id. The item-level `adminOnly` sidebar
  // flag this carried (on top of `role: 'admin'`) was redundant with the
  // group's own `requiredRole: 'admin'` in groupsConfig.ts AND — because
  // `sidebar.adminOnly` is checked unconditionally, flag on or off — would
  // have hidden this section from a `feature_flags` grantee even under
  // RBAC_V2. Removed so the permission is the only flag-on gate.
  {
    path: 'feature-flags',
    element: <MixpanelFlags />,
    role: 'admin',
    permission: 'feature_flags',
    sidebar: { group: 'feature-flags', label: 'Feature Flags' },
  },
];
